import { useEffect, useRef, useState } from 'react';
import {
  ArrowUpRight,
  ClipboardList,
  Heart,
  MapPin,
  Printer,
  RotateCcw,
  ZoomIn,
  Save,
} from 'lucide-react';
import AccessLink from '@/components/common/AccessLink';
import { useAuth } from '@/contexts/AuthContext';
import PageHeader from '@/components/common/PageHeader';
import { Input, Select, Textarea } from '@/components/ui/FormControls';
import AddressTemplateSheets from '@/components/printing/AddressTemplateSheets';
import SalesGreetingCards from '@/components/sales/SalesGreetingCards';
import { defaultTemplateSender, templateCopyCount } from '@/lib/printTemplates';
import { buildGreetingCards, type GreetingCard } from '@/lib/saleGreetings';
import { printHtmlContent } from '@/utils/printUtils';
import { usePrintSenderSettings } from '@/hooks/usePrintSenderSettings';

const buttonClass =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-border px-3 text-sm font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50';

function PrintTemplatesWorkspace() {
  const { can, organization, role } = useAuth();
  const [template, setTemplate] = useState<'address' | 'greeting'>('address');
  const [quantity, setQuantity] = useState('6');
  const [perPage, setPerPage] = useState<3 | 4>(3);
  const [includeSender, setIncludeSender] = useState(true);
  const senderSettings = usePrintSenderSettings(organization!.id, role === 'owner');
  const { sender, setSender } = senderSettings;
  const [cards, setCards] = useState<GreetingCard[] | null>(null);
  const [actualSize, setActualSize] = useState(false);
  const [scale, setScale] = useState(1);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState('');
  const preview = useRef<HTMLDivElement>(null);
  const operation = useRef<AbortController | null>(null);
  const mounted = useRef(true);
  const greetingButton = useRef<HTMLButtonElement>(null);
  const copies = templateCopyCount(quantity);
  const senderInvalid = includeSender && (!sender.name.trim() || !sender.address.trim());
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      operation.current?.abort();
    };
  }, []);
  useEffect(() => {
    const container = preview.current;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) =>
      setScale(Math.min(1, Math.max(0.1, entry!.contentRect.width / ((190 * 96) / 25.4)))),
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [template]);

  async function printAddresses() {
    if (
      !copies ||
      senderInvalid ||
      senderSettings.loading ||
      senderSettings.saving ||
      !can('sales.view') ||
      operation.current
    )
      return;
    const content = document.getElementById('address-template-sheets');
    if (!content) return;
    const controller = new AbortController();
    operation.current = controller;
    setPrinting(true);
    setError('');
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = () => {
      if (operation.current === controller) operation.current = null;
      if (mounted.current) setPrinting(false);
    };
    try {
      await Promise.race([
        Promise.all(
          Array.from(content.querySelectorAll('img')).map((image) => image.decode()),
        ).catch(() => {
          throw new Error('The brand logo could not be loaded. Check your connection and retry.');
        }),
        new Promise((_, reject) => {
          timer = setTimeout(
            () =>
              reject(
                new Error('The brand logo could not be loaded. Check your connection and retry.'),
              ),
            5000,
          );
        }),
      ]);
      if (controller.signal.aborted || !mounted.current) return;
      const overflow = Array.from(content.querySelectorAll<HTMLElement>('.pt-from, .pt-to')).some(
        (element) =>
          element.scrollHeight > element.clientHeight + 1 ||
          element.scrollWidth > element.clientWidth + 1,
      );
      if (overflow)
        throw new Error(
          'These address details do not fit the label. Choose three labels per sheet or shorten the sender address.',
        );
      printHtmlContent(content.outerHTML, {
        documentTitle: 'KLOTHIQ delivery address templates',
        signal: controller.signal,
        onAfterPrint: finish,
      });
    } catch (failure) {
      if (mounted.current && !controller.signal.aborted)
        setError(
          failure instanceof Error
            ? failure.message
            : 'Print could not be prepared. Please try again.',
        );
      finish();
    } finally {
      clearTimeout(timer);
    }
  }

  function previewGreetings() {
    if (!copies || !can('sales.view')) return;
    setCards(
      buildGreetingCards(
        Array.from({ length: copies }, (_, i) => ({
          id: `template-${i + 1}`,
          number: '',
          customerName: null,
        })),
      ),
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Print templates"
        description="Branded paper templates, ready to print, cut and fill in by hand."
      />
      <div className="grid gap-3 sm:grid-cols-2" aria-label="Choose a print template">
        {(
          [
            [
              'address',
              'Delivery address labels',
              'FROM and TO, with optional sender details and room to write.',
              '3 or 4 labels per A4 sheet',
              MapPin,
            ],
            [
              'greeting',
              'Greeting cards',
              'A thoughtful thank-you with a blank customer-name line and our three QR codes.',
              '2 cards per A4 sheet · 100 greetings',
              Heart,
            ],
          ] as const
        ).map(([id, title, description, detail, Icon]) => (
          <button
            key={id}
            type="button"
            aria-pressed={template === id}
            disabled={printing}
            onClick={() => {
              setTemplate(id);
              setError('');
            }}
            className={`obsidian-surface obsidian-interactive flex gap-3 rounded-2xl border p-4 text-left transition-colors disabled:opacity-50 ${template === id ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-border bg-card hover:border-primary/40'}`}
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Icon className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold">{title}</span>
              <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                {description}
              </span>
              <span className="mt-2 block text-xs font-medium text-primary">{detail}</span>
            </span>
          </button>
        ))}
      </div>
      <AccessLink
        to="/deliveries"
        className="obsidian-surface obsidian-interactive flex flex-col justify-between gap-3 rounded-2xl border border-border bg-card p-4 transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:flex-row sm:items-center"
      >
        <span className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <ClipboardList className="h-5 w-5" aria-hidden="true" />
          </span>
          <span>
            <span className="block text-sm font-semibold">Courier handover</span>
            <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
              Prepare a courier-grouped A4 worksheet from selected Packed/Dispatched deliveries,
              with tracking references and handwritten acknowledgement fields.
            </span>
          </span>
        </span>
        <span className="inline-flex min-h-11 shrink-0 items-center gap-2 text-sm font-semibold text-cyan-700 dark:text-primary">
          Choose shipments
          <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
        </span>
      </AccessLink>
      <div
        className={`grid items-start gap-5 ${template === 'address' ? 'xl:grid-cols-[minmax(18rem,2fr)_minmax(0,3fr)]' : ''}`}
      >
        <section
          className="obsidian-surface min-w-0 rounded-2xl border border-border bg-card p-4 sm:p-5"
          aria-label="Template settings"
        >
          <h2 className="text-base font-semibold">
            {template === 'address' ? 'Address label settings' : 'Greeting card settings'}
          </h2>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            No sale is needed. Recipient details stay blank for handwriting.
          </p>
          <fieldset disabled={printing || senderSettings.saving} className="mt-4 min-w-0 space-y-4">
            <label className="block text-sm font-medium">
              Number of copies
              <Input
                type="number"
                min={1}
                max={100}
                step={1}
                required
                value={quantity}
                onChange={(event) => {
                  setQuantity(event.target.value);
                  setError('');
                }}
                helpLabel="Number of copies"
                aria-invalid={!copies}
                aria-describedby="template-quantity-help"
                className="mt-1"
              />
            </label>
            <p
              id="template-quantity-help"
              className={`text-xs ${copies ? 'text-muted-foreground' : 'text-destructive'}`}
            >
              {copies ? 'Choose 1 to 100 labels or cards.' : 'Enter a whole number from 1 to 100.'}
            </p>
            {template === 'address' && (
              <>
                <label className="block text-sm font-medium">
                  Labels per A4 sheet
                  <Select
                    value={perPage}
                    onChange={(event) => {
                      setPerPage(Number(event.target.value) as 3 | 4);
                      setError('');
                    }}
                    helpLabel="Labels per A4 sheet"
                    className="mt-1 w-full"
                  >
                    <option value="3">3 · Roomy writing space</option>
                    <option value="4">4 · Compact labels</option>
                  </Select>
                </label>
                <label className="flex min-h-11 items-center gap-2 text-sm font-medium">
                  <Input
                    type="checkbox"
                    checked={includeSender}
                    onChange={(event) => {
                      setIncludeSender(event.target.checked);
                      setError('');
                    }}
                    helpLabel="Print sender details"
                  />
                  Print sender details
                </label>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {includeSender
                    ? 'Review our sender address below, including the full postal address and PIN. Save it to reuse it for future prints.'
                    : 'FROM and TO will both have blank lines. The logo and branding stay visible.'}
                </p>
                {includeSender && (
                  <fieldset
                    disabled={senderSettings.loading}
                    className="min-w-0 space-y-3 rounded-xl border border-border bg-background/40 p-3"
                  >
                    <legend className="px-1 text-sm font-semibold">Our sender address</legend>
                    <p role="status" className="text-xs text-muted-foreground">
                      {senderSettings.loading
                        ? 'Loading saved address…'
                        : !senderSettings.ready
                          ? 'Saved address unavailable'
                          : senderSettings.dirty
                            ? 'Unsaved changes'
                            : senderSettings.saved
                              ? 'Using saved address'
                              : 'No saved address yet'}
                    </p>
                    <label className="block text-sm">
                      Sender name
                      <Input
                        value={sender.name}
                        maxLength={80}
                        required
                        onChange={(event) => {
                          setSender({ ...sender, name: event.target.value });
                          setError('');
                        }}
                        helpLabel="Sender name"
                        aria-invalid={!sender.name.trim()}
                        className="mt-1"
                      />
                    </label>
                    <label className="block text-sm">
                      Full sender address
                      <Textarea
                        value={sender.address}
                        maxLength={300}
                        rows={4}
                        required
                        onChange={(event) => {
                          setSender({ ...sender, address: event.target.value });
                          setError('');
                        }}
                        helpLabel="Full sender address"
                        aria-invalid={!sender.address.trim()}
                        placeholder="Building, street, locality, city, state and PIN"
                        className="mt-1"
                      />
                    </label>
                    <label className="block text-sm">
                      Sender phone <span className="text-muted-foreground">(optional)</span>
                      <Input
                        type="tel"
                        value={sender.phone}
                        maxLength={30}
                        onChange={(event) => {
                          setSender({ ...sender, phone: event.target.value });
                          setError('');
                        }}
                        helpLabel="Sender phone"
                        className="mt-1"
                      />
                    </label>
                    {senderInvalid && (
                      <p role="alert" className="text-xs text-destructive">
                        Enter a sender name and address, or turn off printed sender details.
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {role === 'owner' && (
                        <button
                          type="button"
                          disabled={
                            !senderSettings.ready ||
                            !sender.name.trim() ||
                            !sender.address.trim() ||
                            (!senderSettings.dirty && !!senderSettings.saved)
                          }
                          onClick={() => void senderSettings.save()}
                          className={`${buttonClass} border-primary bg-primary text-primary-foreground hover:bg-primary/90`}
                        >
                          <Save className="h-4 w-4" />
                          {senderSettings.saving ? 'Saving address…' : 'Save address'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => void senderSettings.reload()}
                        className={buttonClass}
                      >
                        Reload saved address
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSender(defaultTemplateSender());
                          setError('');
                        }}
                        className={buttonClass}
                      >
                        <RotateCcw className="h-4 w-4" />
                        Restore brand details
                      </button>
                    </div>
                    {role !== 'owner' && (
                      <p className="text-xs text-muted-foreground">
                        Only Super Admin can save the shared sender address. You can edit a draft
                        for this print.
                      </p>
                    )}
                    {senderSettings.notice && (
                      <p role="status" className="text-sm text-emerald-600 dark:text-emerald-400">
                        {senderSettings.notice}
                      </p>
                    )}
                    {senderSettings.error && (
                      <p
                        role="alert"
                        className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive"
                      >
                        {senderSettings.error}
                      </p>
                    )}
                  </fieldset>
                )}
              </>
            )}
          </fieldset>
          {copies && (
            <p className="mt-4 rounded-xl bg-muted/40 p-3 text-sm">
              <strong>
                {copies} {template === 'address' ? 'labels' : 'cards'}
              </strong>{' '}
              on {Math.ceil(copies / (template === 'address' ? perPage : 2))} A4{' '}
              {Math.ceil(copies / (template === 'address' ? perPage : 2)) === 1
                ? 'sheet'
                : 'sheets'}
              .{' '}
              {template === 'address'
                ? 'Cut between labels, then fill in TO.'
                : 'Write the customer name after printing.'}
            </p>
          )}
          <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
            Print on A4, portrait, at 100% scale. Turn off browser headers and footers. You can also
            choose Save as PDF in the print dialog.
          </p>
          {error && (
            <p
              role="alert"
              className="mt-3 rounded-xl bg-destructive/10 p-3 text-sm text-destructive"
            >
              {error}
            </p>
          )}
          {printing && (
            <p role="status" className="mt-3 text-sm text-muted-foreground">
              Preparing address labels…
            </p>
          )}
          {template === 'address' ? (
            <button
              type="button"
              disabled={
                !copies ||
                senderInvalid ||
                printing ||
                senderSettings.loading ||
                senderSettings.saving
              }
              onClick={() => void printAddresses()}
              className={`${buttonClass} mt-4 w-full border-primary bg-primary text-primary-foreground hover:bg-primary/90`}
            >
              <Printer className="h-4 w-4" />
              {printing ? 'Preparing print…' : 'Print address labels'}
            </button>
          ) : (
            <button
              ref={greetingButton}
              type="button"
              disabled={!copies}
              onClick={previewGreetings}
              className={`${buttonClass} mt-4 border-primary bg-primary text-primary-foreground hover:bg-primary/90`}
            >
              <Heart className="h-4 w-4" />
              Preview greeting cards
            </button>
          )}
        </section>
        {template === 'address' && (
          <section
            className="min-w-0 rounded-2xl border border-border bg-muted/20 p-3 sm:p-4"
            aria-label="Address label preview"
          >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold">Print preview</h2>
              <button
                type="button"
                className={buttonClass}
                aria-pressed={actualSize}
                onClick={() => setActualSize(!actualSize)}
              >
                <ZoomIn className="h-4 w-4" />
                {actualSize ? 'Fit to width' : 'Actual size'}
              </button>
            </div>
            {actualSize && (
              <p className="mb-3 text-xs text-muted-foreground">
                Scroll within the preview to see the full sheet.
              </p>
            )}
            <div
              ref={preview}
              className="max-h-[75dvh] overflow-auto rounded-lg bg-muted/40"
              tabIndex={0}
              aria-label="Scrollable A4 address sheets"
            >
              {copies ? (
                <div style={{ zoom: actualSize ? 1 : scale }}>
                  <AddressTemplateSheets
                    copies={copies}
                    perPage={perPage}
                    sender={sender}
                    includeSender={includeSender}
                  />
                </div>
              ) : (
                <p className="p-6 text-sm text-muted-foreground">
                  Choose a valid number of copies to see the preview.
                </p>
              )}
            </div>
          </section>
        )}
      </div>
      {cards && (
        <SalesGreetingCards
          cards={cards}
          blankRecipient
          onClose={() => {
            setCards(null);
            requestAnimationFrame(() => greetingButton.current?.focus());
          }}
        />
      )}
    </div>
  );
}

export default function PrintTemplatesPage() {
  const { organization, profile, access, can } = useAuth();
  if (!organization?.id || !can('sales.view'))
    return (
      <div role="alert" className="obsidian-surface rounded-xl border border-border bg-card p-5">
        Print templates are not available for this login.
      </div>
    );
  return (
    <PrintTemplatesWorkspace
      key={JSON.stringify([organization.id, profile?.id, access?.access_version])}
    />
  );
}
